import React, { useState } from "react";
import { pageTitle } from "../../helpers/PageTitle";
import Section from "../Section";
import Spacing from "../Spacing";
import {
  BsShieldCheck,
  BsPersonBadge,
  BsHeadset,
  BsPlus,
  BsDash,
  BsArrowRightShort,
  BsThreeDots,
  BsCollection
} from "react-icons/bs";

const faqData = [
  {
    id: "general",
    title: "General Questions",
    subtitle: "Category 01",
    icon: <BsShieldCheck />,
    items: [
      {
        question: "What is GKC Klinica?",
        answer: "GKC Klinica is a technology company focused on improving access to healthcare services. It enables patients to consult licensed healthcare professionals quickly and offers end-to-end healthcare and wellness solutions."
      },
      {
        question: "What services does GKC Klinica offer?",
        answer: "It offers online consultations, appointment scheduling, diagnostic packages, digital prescriptions, medicine delivery support, blood donation, wellness programs, insurance assistance, and medical tourism services."
      },
      {
        question: "Is GKC Klinica available across India?",
        answer: "Yes, GKC Klinica operates across India, providing healthcare services through its digital platform."
      },
      {
        question: "How is GKC Klinica different from other healthcare apps?",
        answer: "It integrates consultations, diagnostics, wellness, insurance, and medical tourism into one seamless platform, offering a complete healthcare ecosystem."
      },
      {
        question: "Is GKC Klinica safe and reliable?",
        answer: "Yes, it follows strict privacy standards and uses secure technologies, including optional privacy features for confidential consultations."
      },
      {
        question: "How long does an online consultation last?",
        answer: "Consultations typically last 15–30 minutes depending on the medical issue and doctor specialization."
      },
      {
        question: "Can I chat with the same doctor again?",
        answer: "Yes, you can consult the same doctor again based on availability and your consultation history."
      },
      {
        question: "Will I receive a digital prescription?",
        answer: "Yes, doctors provide digital prescriptions after consultation if medication is required."
      },
      {
        question: "Is an online prescription legally valid in India?",
        answer: "Yes, digital prescriptions are legally valid if issued by a registered medical practitioner as per telemedicine guidelines."
      },
      {
        question: "What conditions can be treated via online consultation?",
        answer: "Non-emergency conditions like fever, infections, skin issues, digestion problems, chronic follow-ups, stress, and second opinions."
      },
      {
        question: "Can I use GKC Klinica for my family members?",
        answer: "Yes, the platform supports healthcare services for all age groups including children and elderly patients."
      },
      {
        question: "Is my medical data secure on GKC Klinica?",
        answer: "Yes, the platform uses secure systems and follows privacy protocols to protect user data."
      },
      {
        question: "Does GKC Klinica store medical records?",
        answer: "Yes, it securely stores prescriptions, reports, and consultation history for easy access."
      },
      {
        question: "How can I contact GKC Klinica customer support?",
        answer: "You can contact support via the official email ID or phone number provided on the website."
      },
      {
        question: "Does GKC Klinica offer health packages?",
        answer: "Yes, it offers packages like Mini, Basic, Premium, Platinum, and Comprehensive health packages."
      },
      {
        question: "Is GKC Klinica suitable for children and elderly patients?",
        answer: "Yes, it provides specialized care and packages for both children and elderly patients."
      },
      {
        question: "Can I reschedule or cancel appointments?",
        answer: "Yes, appointments can be managed easily through the app, including rescheduling or cancellation."
      },
      {
        question: "Does GKC Klinica provide second medical opinions?",
        answer: "Yes, users can consult experienced doctors for second opinions on diagnoses and treatments."
      },
      {
        question: "How does GKC Klinica ensure doctor verification?",
        answer: "Doctors are verified through credential checks, registration validation, and experience review before onboarding."
      },
      {
        question: "Is GKC Klinica available in regional languages?",
        answer: "Yes, the platform supports multiple Indian regional languages for better accessibility."
      },
      {
        question: "Can rural patients use GKC Klinica?",
        answer: "Yes, rural users can access consultations via video, audio, or chat without traveling."
      },
      {
        question: "Does GKC Klinica provide home healthcare services?",
        answer: "Yes, services like home nurse visits, lab sample collection, and basic health monitoring are available in selected areas."
      },
      {
        question: "Are there membership plans in GKC Klinica?",
        answer: "Yes, membership plans offer benefits like discounted consultations, priority bookings, and special pricing."
      },
      {
        question: "How do I update my profile details?",
        answer: "You can update your profile by logging in, going to the profile section, editing details, and saving changes."
      },
      {
        question: "Why should I choose GKC Klinica over other medical platforms?",
        answer: "It offers verified doctors, secure consultations, affordable pricing, rural access, compliant prescriptions, and integrated healthcare services."
      }
    ]
  },
  {
    id: "account",
    title: "Online Doctor Consultation ",
    subtitle: "Category 02",
    icon: <BsPersonBadge />,
    items: [
      {
        question: "How can I consult a doctor online through GKC Klinica?",
        answer: "You can consult a doctor online through the GKC Klinica app. Open the app, go to ‘Our Care Team’ section, select a specialist, choose your city, and book an appointment based on available time slots. Details will be available in the ‘Appointments’ section."
      },
      {
        question: "Is online consultation on GKC Klinica free?",
        answer: "No, online consultation is not completely free but is affordable compared to other platforms. Discounts and offers may also be available."
      },
      {
        question: "What specialties are available for online consultation?",
        answer: "Specialties include General Medicine, Pediatrics, Gynecology, Dermatology, Cardiology, Orthopedics, Psychiatry, Nutrition & Diet, Urology, ENT, and more, along with diagnostics and health packages."
      },
      {
        question: "Can I choose my preferred doctor?",
        answer: "Yes, you can choose your preferred doctor from the list of available specialists before booking your appointment."
      },
      {
        question: "Are GKC Klinica doctors qualified and verified?",
        answer: "Yes, all doctors are qualified and verified with proper credentials, experience, and valid registrations."
      },
      {
        question: "How long does an online consultation last?",
        answer: "Typically, consultations last 10–20 minutes, depending on the medical condition and doctor."
      },
      {
        question: "Can I chat with the same doctor again?",
        answer: "Yes, you can book follow-up consultations with the same doctor for continuity of care."
      },
      {
        question: "What conditions can be treated via online consultation?",
        answer: "Common conditions include fever, infections, skin issues, digestive problems, mental health concerns, and chronic diseases like diabetes and hypertension."
      },
      {
        question: "Can I consult pediatricians online?",
        answer: "Yes, you can consult pediatricians for children's health issues like fever, infections, and growth monitoring."
      },
      {
        question: "Is online consultation safe for chronic diseases?",
        answer: "Yes, it is safe for managing chronic conditions through follow-ups, digital prescriptions, and monitoring."
      },
      {
        question: "Can I upload medical reports before consultation?",
        answer: "Yes, you can upload reports, prescriptions, and lab results securely before consultation."
      },
      {
        question: "Does GKC Klinica record calls?",
        answer: "No, calls are not recorded without patient consent. Privacy and confidentiality are maintained."
      },
      {
        question: "Is video consultation available?",
        answer: "Yes, secure video consultation is available and required for certain conditions as per telemedicine guidelines."
      },
      {
        question: "What happens if the doctor doesn’t respond?",
        answer: "Support will assist by rescheduling or assigning another doctor to ensure timely consultation."
      },
      {
        question: "Can I get a second opinion online?",
        answer: "Yes, you can consult another doctor by booking a new appointment and sharing your reports."
      },
      {
        question: "Are follow-up consultations charged separately?",
        answer: "Yes, charges depend on the doctor and service. Details are shown before booking."
      },
      {
        question: "Can elderly patients use online consultation easily?",
        answer: "Yes, the platform is user-friendly, and family members can assist elderly patients."
      },
      {
        question: "What payment methods are accepted?",
        answer: "Payments can be made via UPI, debit/credit cards, net banking, and other secure digital options."
      },
      {
        question: "Is my consultation private and confidential?",
        answer: "Yes, consultations are handled securely with strict privacy protocols, though no system guarantees absolute security."
      },
      {
        question: "Can I consult doctors 24x7?",
        answer: "Yes, consultations are available 24x7 depending on doctor availability. Pre-booking is recommended."
      },
      {
        question: "How quickly will a doctor respond?",
        answer: "Doctors respond during the scheduled appointment time selected during booking."
      },
      {
        question: "Can I get medicine delivered after consultation?",
        answer: "No, GKC Klinica does not provide medicine delivery services."
      },
      {
        question: "What if I am not satisfied with the consultation?",
        answer: "You can contact customer support to report your concern, and the team will assist you with possible solutions."
      }
    ]
  },
  {
    id: "support",
    title: "Diagnostics & Preventive Care ",
    subtitle: "Category 03",
    icon: <BsHeadset />,
    items: [
      {
        question: "Can I book lab tests through GKC Klinica?",
        answer: "Yes, you can book lab tests through GKC Klinica. A lab professional will visit your home to collect the sample at your selected time slot. Reports are usually available in 24–48 hours."
      },
      {
        question: "Does GKC Klinica offer home sample collection?",
        answer: "Yes, GKC Klinica provides home sample collection. A trained phlebotomist will visit your home, collect samples, and send them for testing."
      },
      {
        question: "How soon will I receive my lab reports?",
        answer: "Most lab reports are available within 24–48 hours after sample collection. Some tests may take longer depending on processing time."
      },
      {
        question: "Can I book full body health check-up packages?",
        answer: "Yes, you can book full body health check-up packages. GKC Klinica offers comprehensive and affordable packages in partnership with trusted labs."
      },
      {
        question: "Does GKC Klinica provide preventive health plans?",
        answer: "Yes, GKC Klinica offers preventive health plans like Mini, Basic, Premium, Platinum, and Comprehensive packages, including screenings and wellness check-ups."
      },
      {
        question: "Can I order medicines through GKC Klinica?",
        answer: "No, GKC Klinica does not provide medicine delivery. You will receive a digital prescription to purchase medicines from your preferred pharmacy."
      },
      {
        question: "Is medicine delivery available in my city?",
        answer: "No, medicine delivery is not currently available. You can use your digital prescription to buy medicines from any pharmacy."
      },
      {
        question: "Are discounts available on lab tests?",
        answer: "Yes, GKC Klinica offers discounts and voucher-based offers on lab tests and health services, making them more affordable."
      },
      {
        question: "Does GKC Klinica offer pediatric health packages?",
        answer: "No dedicated pediatric packages are available currently, but you can consult pediatricians and access tests for children."
      },
      {
        question: "Are vaccination services available?",
        answer: "Yes, you can book vaccination appointments at nearby partner clinics or hospitals and track schedules through the app."
      },
      {
        question: "Does GKC Klinica offer chronic disease management plans?",
        answer: "Yes, GKC Klinica supports chronic disease management with consultations, monitoring, lab tests, and personalized care plans."
      },
      {
        question: "Can I schedule recurring lab tests?",
        answer: "Yes, you can schedule recurring lab tests and set reminders for regular health monitoring."
      },
      {
        question: "How do I download my lab reports?",
        answer: "You can download reports from the app or website by visiting the 'Records' section and selecting the required report."
      }
    ]
  },
  {
    id: "miscellaneous",
    title: "Miscellaneous Questions",
    subtitle: "Category 01",
    icon: <BsCollection />,
    items: [
      {
        question: "How do I create an account on GKC Platform ?",
        answer: `
      1. Visit the GKC Klinica Website
      2. Choose Doctor Registration.
      3. Fill in Required Information: mobile number, email ID, address, name, date of birth, specialization, and gender.
      4. Verify Your Account.
      5. Complete Your Profile.
    `,
      },
      {
        question: "What information do I need to provide to register on the GKC Klinica platform?",
        answer:
          "To register, provide your mobile number, email ID, address, name, date of birth, specialization, and gender. Then complete your profile with education, registration number, location, achievements, awards, and OPD timing. For help, contact support@gkcklinica.com.",
      },
      {
        question: "How do I reset my password?",
        answer:
          'Click "Forgot Password" on the login screen, enter your mobile/email, and use the received code to set a new password.',
      },
      {
        question: "Can I access my medical records on the app?",
        answer:
          "Yes! GKC Klinica allows secure storage and access to prescriptions, lab results, and doctor notes (with permission).",
      },
      {
        question: "How do I schedule an appointment with a doctor?",
        answer:
          "Search doctors by specialty and location, view their profiles, and book appointments in the app.",
      },
      {
        question: "Can I communicate with my doctor through the app?",
        answer:
          "Absolutely! Secure messaging is available to connect with your doctor.",
      },
      {
        question: "How do I track my medical history and test results?",
        answer:
          "The apps medical records section shows history including past consultations, prescriptions, and shared test results.",
      },
      {
        question: "Is my personal and medical information secure on the app?",
        answer:
          "Yes, we use industry-standard security to protect your data and ensure confidentiality.",
      },
      {
        question: "How do I find a hospital or healthcare provider on the app?",
        answer:
          "Use the search feature with filters like location, specialty, and rating.",
      },
      {
        question: "Can I search for hospitals by location or specialty?",
        answer:
          "Yes, filter hospitals by location or their specialty (e.g., cardiology, pediatrics).",
      },
      {
        question: "How do I view hospital profiles and ratings?",
        answer:
          "Each hospital profile shows services, location, and patient ratings.",
      },
      {
        question: "How do I access my medical records from a hospital?",
        answer:
          "Doctors share records in the app. To access hospital-held records, contact the hospital directly.",
      },

      {
        question: "How do I provide feedback or review a hospital or doctor?",
        answer:
          "Leave ratings and reviews for doctors through the platform to help others",
      },

      {
        question: "What services does the app offer?",
        answer:
          "Doctor discovery, appointment booking, secure messaging, and medical record storage/access.",
      },

      {
        question: "Is the app free to use?",
        answer:
          "Basic features are free. Consultations may incur fees depending on doctor and insurance.",
      },

      {
        question: "How do I contact customer support?",
        answer: "Contact support through the app or via the website for help.",
      },

      {
        question: "Is my data secure and confidential?",
        answer:
          "Yes, your data is encrypted and protected using industry best practices.",
      },

      {
        question: "How do I update my personal information?",
        answer: "Go to the app’s settings and update your details easily.",
      },

      {
        question: "Can I use the app on multiple devices?",
        answer: "Yes, sign in on any device with your credentials.",
      },

      {
        question: "How do I delete my account?",
        answer:
          "Use app settings to delete your account. Note that all stored records will also be deleted.",
      },
    ]
  }
];

export default function Faq() {
  pageTitle("FAQ");

  // State for which Category is currently visible
  const [activeCategory, setActiveCategory] = useState("general");
  // State for which Accordion item is open
  const [activeItem, setActiveItem] = useState(0);

  const handleCategoryChange = (id) => {
    setActiveCategory(id);
    setActiveItem(0); // Reset accordion to open the first item of the new category
  };

  const toggleAccordion = (index) => {
    setActiveItem(activeItem === index ? null : index);
  };

  // Find the current data object based on state
  const currentFaq = faqData.find((cat) => cat.id === activeCategory);

  return (
    <Section topMd={160}
      topLg={70}
      topXl={50}>
      <Section topMd={170} bottomMd={60} className="cs_faq_hero">
        <div className="container text-center">
          <h1 className="cs_faq_main_title">Frequently Asked Questions</h1>
          <p className="cs_faq_description">Select a category to find the information you need.</p>
        </div>
      </Section>

      <div className="container">
        <div className="row">
          {/* 1. Sidebar with Filter Logic */}
          <div className="col-lg-4 col-xl-3">
            <div className="cs_faq_sidebar">
              <h5 className="cs_sidebar_title">FAQ Categories</h5>
              <div className="cs_sidebar_links">
                {faqData.map((cat) => (
                  <button
                    key={cat.id}
                    className={`cs_sidebar_link_btn ${activeCategory === cat.id ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(cat.id)}
                  >
                    <span className="cs_icon_box">{cat.icon}</span>
                    <span className="cs_btn_text">{cat.title}</span>
                    <BsArrowRightShort className="cs_arrow_icon" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 2. Content Area (Only shows activeCategory) */}
          <div className="col-lg-8 col-xl-9">
            <div className="cs_faq_group animate_fade_in">
              <div className="cs_category_header">
                {/* <span className="cs_category_subtitle">{currentFaq.subtitle}</span> */}
                <h2 className="cs_category_title">{currentFaq.title}</h2>
                <div className="cs_heading_line"></div>
              </div>

              <div className="cs_accordion_container">
                {currentFaq.items.map((item, index) => {
                  const isOpen = activeItem === index;

                  return (
                    <div key={index} className={`cs_faq_item ${isOpen ? 'active' : ''}`}>
                      <button
                        className="cs_faq_question"
                        onClick={() => toggleAccordion(index)}
                      >
                        <span>{item.question}</span>
                        <span className="cs_accordion_icon">
                          {isOpen ? <BsDash /> : <BsPlus />}
                        </span>
                      </button>
                      <div className={`cs_faq_answer ${isOpen ? 'show' : ''}`}>
                        <div className="cs_answer_inner">
                          {item.answer}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Spacing md="120" />
    </Section>
  );
}